import { useEffect, useMemo, useState } from 'react';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { QueryErrorState } from '../components/dashboard/QueryErrorState';
import { QueryLoadingBlock } from '../components/dashboard/QueryLoadingBlock';
import { api } from '../lib/api';
import { useQueryRetry } from '../lib/dashboard/useQueryRetry';
import { findScheduleConflicts } from '../lib/schedule/findScheduleConflicts';
import { useOrganisationId } from '../lib/session';
import { DaypartDrawer } from './schedule/DaypartDrawer';
import { ScheduleDaypartsCard } from './schedule/ScheduleDaypartsCard';
import { ScheduleStatusSidebar } from './schedule/ScheduleStatusSidebar';
import { ScheduleWeekTimeline } from './schedule/ScheduleWeekTimeline';

/**
 * Schedule page — week calendar + dayparts + create/edit drawer
 * (Ghost Screen Schedule.dc.html).
 */
export function SchedulePage() {
  const { t } = useTranslation('schedule');
  const organisationId = useOrganisationId();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const focusedScreenId = searchParams.get('screenId');
  const focusedGroupId = searchParams.get('groupId');
  const newOpen = searchParams.get('new') === '1';
  const [editId, setEditId] = useState<string | null>(null);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const setNewOpen = (open: boolean) => {
    const next = new URLSearchParams(searchParams);
    if (open) next.set('new', '1');
    else next.delete('new');
    setSearchParams(next, { replace: true });
  };

  const openEdit = (scheduleId: string) => {
    setNewOpen(false);
    setEditId(scheduleId);
  };

  const closeDrawer = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('new');
    next.delete('groupId');
    setSearchParams(next, { replace: true });
    setEditId(null);
  };

  const schedulesQuery = useQuery({
    queryKey: ['schedules', organisationId],
    queryFn: () => api.listSchedules(organisationId),
  });
  const screensQuery = useQuery({
    queryKey: ['screens', organisationId],
    queryFn: () => api.listScreens(organisationId),
  });
  const groupsQuery = useQuery({
    queryKey: ['screen-groups', organisationId],
    queryFn: () => api.listScreenGroups(organisationId),
  });
  const playlistsQuery = useQuery({
    queryKey: ['playlists', organisationId],
    queryFn: () => api.listPlaylists(organisationId),
  });
  const locationsQuery = useQuery({
    queryKey: ['locations', organisationId],
    queryFn: () => api.listLocations(organisationId),
  });

  const screens = screensQuery.data ?? [];
  const groups = groupsQuery.data ?? [];
  const playlists = playlistsQuery.data ?? [];
  const locations = locationsQuery.data ?? [];
  const schedules = schedulesQuery.data ?? [];

  const groupIdsOnSchedules = useMemo(
    () =>
      [
        ...new Set(
          schedules
            .map((schedule) => schedule.screenGroupId)
            .filter((id): id is string => Boolean(id)),
        ),
      ],
    [schedules],
  );

  const membershipQueries = useQueries({
    queries: groupIdsOnSchedules.map((screenGroupId) => ({
      queryKey: ['screen-group', organisationId, screenGroupId],
      queryFn: () => api.getScreenGroup(organisationId, screenGroupId),
    })),
  });

  const groupDetails = useMemo(
    () =>
      membershipQueries
        .map((query) => query.data)
        .filter((group): group is NonNullable<typeof group> => Boolean(group)),
    [membershipQueries],
  );

  const groupMembersById = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const group of groupDetails) {
      map.set(group.id, group.screenIds);
    }
    return map;
  }, [groupDetails]);

  const conflicts = useMemo(
    () =>
      findScheduleConflicts({
        schedules,
        screens,
        groups: groupDetails.length > 0 ? groupDetails : groups,
      }),
    [schedules, screens, groupDetails, groups],
  );

  const editingSchedule = editId
    ? (schedules.find((schedule) => schedule.id === editId) ?? null)
    : null;
  const drawerOpen = newOpen || Boolean(editingSchedule);

  const invalidateSchedules = async () => {
    await queryClient.invalidateQueries({ queryKey: ['schedules', organisationId] });
    await queryClient.invalidateQueries({ queryKey: ['schedule-resolve', organisationId] });
  };

  const loading =
    schedulesQuery.isPending ||
    screensQuery.isPending ||
    groupsQuery.isPending ||
    playlistsQuery.isPending ||
    locationsQuery.isPending;
  const hasError =
    schedulesQuery.isError ||
    screensQuery.isError ||
    groupsQuery.isError ||
    playlistsQuery.isError ||
    locationsQuery.isError;

  const { isRetrying, retry } = useQueryRetry(async () => {
    await Promise.all([
      schedulesQuery.refetch(),
      screensQuery.refetch(),
      groupsQuery.refetch(),
      playlistsQuery.refetch(),
      locationsQuery.refetch(),
    ]);
  });

  // Keep create-from-URL working when landing with ?new=1.
  useEffect(() => {
    if (newOpen) setEditId(null);
  }, [newOpen]);

  return (
    <div className="flex flex-col gap-5" style={{ animation: 'gs-in 260ms ease both' }}>
      {hasError || isRetrying ? (
        <QueryErrorState
          message={t('loadError')}
          onRetry={retry}
          isRetrying={isRetrying}
        />
      ) : loading ? (
        <QueryLoadingBlock />
      ) : (
        <>
          <ScheduleWeekTimeline
            schedules={schedules}
            now={now}
            onOpenSchedule={openEdit}
          />

          <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(280px,0.9fr)]">
            <ScheduleDaypartsCard
              schedules={schedules}
              screens={screens}
              groups={groups}
              playlists={playlists}
              locations={locations}
              now={now}
              onOpen={openEdit}
            />
            <ScheduleStatusSidebar
              schedules={schedules}
              conflicts={conflicts}
              now={now}
              groupMembersById={groupMembersById}
            />
          </div>
        </>
      )}

      <DaypartDrawer
        open={drawerOpen}
        organisationId={organisationId}
        schedule={editingSchedule}
        screens={screens}
        groups={groups}
        playlists={playlists}
        locations={locations}
        initialScreenId={focusedScreenId}
        initialScreenGroupId={focusedGroupId}
        onClose={closeDrawer}
        onSaved={() => {
          void invalidateSchedules();
        }}
      />
    </div>
  );
}
